import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { UserPermissionsManager } from "@/components/users/UserPermissionsManager";
import { usePagination } from "@/hooks/usePagination";
import { UserFormModal } from "@/components/users/UserFormModal";
import { UserTable } from "@/components/users/UserTable";

interface UserProfile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  email: string | null;
  role: string;
  suspended: boolean;
  last_login: string | null;
  assigned_clients: string[] | null;
  assigned_projects: string[] | null;
  profile_image_url: string | null;
  created_at: string;
  manager_id: string | null;
  cost_per_hour: number | null;
  manager?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    full_name: string | null;
  } | null;
}


export default function Users() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSuspended, setSelectedSuspended] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [userForPermissions, setUserForPermissions] = useState<UserProfile | null>(null);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [userToSuspend, setUserToSuspend] = useState<UserProfile | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();

  // Pagination hook
  const {
    currentPage,
    itemsPerPage,
    setCurrentPage,
    getPaginationInfo,
    goToPage,
    goToNextPage,
    goToPrevPage
  } = usePagination({
    totalItems: totalCount,
    initialItemsPerPage: 10
  });

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, selectedSuspended]);

  // Realtime subscription for user changes
  useEffect(() => {
    const channel = supabase
      .channel('users-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'profiles'
        },
        () => {
          console.log('New user profile created, refreshing list...');
          fetchUsers();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles'
        },
        () => {
          console.log('User profile updated, refreshing list...');
          fetchUsers();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'profiles'
        },
        () => {
          console.log('User profile deleted, refreshing list...');
          fetchUsers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      // Query with manager information joined
      let query = supabase
        .from('profiles')
        .select(`
          *,
          manager:manager_id(
            id,
            first_name,
            last_name,
            full_name
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false });

      // Apply server-side filters
      if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }
      
      // Role filtering removed - no longer needed
      
      if (selectedSuspended && selectedSuspended !== "all") {
        query = query.eq('suspended', selectedSuspended === 'suspended');
      }

      // Apply pagination
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        toast({
          title: "Error",
          description: `Failed to fetch users: ${error.message}`,
          variant: "destructive"
        });
        return;
      }

      // Set users data and total count
      setUsers(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      toast({
        title: "Error",
        description: `Unexpected error while fetching users: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    }
  };

  // Remove client-side filtering since we're doing server-side filtering
  const filteredUsers = users;

  const handleEditUser = (user: UserProfile) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleSuspendToggle = async (user: UserProfile) => {
    // Show confirmation dialog for suspension
    if (!user.suspended) {
      setUserToSuspend(user);
      setShowSuspendDialog(true);
      return;
    }

    // Direct unsuspend without confirmation
    await updateUserSuspended(user.user_id, false);
  };

  const updateUserSuspended = async (userId: string, suspended: boolean) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ suspended })
        .eq('user_id', userId);

      if (error) {
        throw error;
      }

      // Refresh users list to get updated data
      fetchUsers();

      toast({
        title: "User updated",
        description: `User ${suspended ? 'suspended' : 'unsuspended'} successfully`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update user",
        variant: "destructive"
      });
    }
  };

  const confirmSuspendUser = async () => {
    if (!userToSuspend) return;
    
    await updateUserSuspended(userToSuspend.user_id, true);
    setShowSuspendDialog(false);
    setUserToSuspend(null);
  };

  const handleDeleteUser = (user: UserProfile) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      // Delete user permissions first
      await supabase
        .from('user_permissions')
        .delete()
        .eq('user_id', userToDelete.user_id);

      // Delete profile
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('user_id', userToDelete.user_id);

      if (error) throw error;

      toast({
        title: "User deleted",
        description: `User ${userToDelete.first_name} ${userToDelete.last_name} has been deleted successfully`,
      });

      fetchUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete user",
        variant: "destructive"
      });
    } finally {
      setShowDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  const openPermissionsModal = (user: UserProfile) => {
    setUserForPermissions(user);
    setIsPermissionsModalOpen(true);
  };

  const { startItem, endItem, totalItems, hasNextPage, hasPrevPage } = getPaginationInfo();

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Users</h1>
        <Button 
          className="bg-primary hover:bg-primary/90"
          onClick={() => setIsAddModalOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Select value={selectedSuspended} onValueChange={setSelectedSuspended}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search users"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Main Table */}
      <UserTable 
        users={filteredUsers}
        onEditUser={handleEditUser}
        onSuspendToggle={handleSuspendToggle}
        onDeleteUser={handleDeleteUser}
        onManagePermissions={openPermissionsModal}
      />

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-muted-foreground">
            Showing {startItem} to {endItem} of {totalItems} users
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPrevPage}
              disabled={!hasPrevPage}
            >
              Previous
            </Button>
            <span className="text-sm">
              Page {currentPage} of {Math.ceil(totalItems / itemsPerPage)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={!hasNextPage}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {filteredUsers.length === 0 && totalItems === 0 && (
        <div className="text-center py-12">
          <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No users found</h3>
          <p className="text-muted-foreground">Try adjusting your filters or search term.</p>
        </div>
      )}

      {/* User Form Modals */}
      <UserFormModal 
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        mode="add"
        users={users}
        onUserCreated={fetchUsers}
        onUserUpdated={fetchUsers}
      />

      <UserFormModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        mode="edit"
        user={selectedUser}
        users={users}
        onUserCreated={fetchUsers}
        onUserUpdated={fetchUsers}
      />

      {/* Suspend User Confirmation Dialog */}
      <AlertDialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to suspend {userToSuspend?.first_name} {userToSuspend?.last_name}? 
              They will no longer be able to access the system until unsuspended.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowSuspendDialog(false);
              setUserToSuspend(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmSuspendUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Suspend User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete {userToDelete?.first_name} {userToDelete?.last_name}? 
              This action cannot be undone and will remove all their data from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDeleteDialog(false);
              setUserToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permissions Manager Modal */}
      <UserPermissionsManager 
        isOpen={isPermissionsModalOpen}
        onClose={() => {
          setIsPermissionsModalOpen(false);
          setUserForPermissions(null);
        }}
        user={userForPermissions}
        onPermissionsUpdated={fetchUsers}
      />
    </div>
  );
}